// Validation rules and required fields for different shipper types

// Shipper types the Shipping Options API supports for webshop checkout
// pickup: the shopper collects the order at the warehouse
export const CHECKOUT_SUPPORTED_SHIPPER_TYPES = ['postnl', 'dhl', 'dhlgermany', 'dpd', 'ups', 'gls', 'trunkrs', 'cycloon', 'hst', 'ampere', 'innosendv2', 'sendcloud', 'pickup'];

export const shipperSupportsCheckout = (type) => CHECKOUT_SUPPORTED_SHIPPER_TYPES.includes(type);

// Innosend brokers several couriers and each is offered as its own checkout option, identified by this key.
// warehouse-api and the Shipping Options API derive the same key from the courier name.
export const checkoutCourierKey = (name) => String(name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Innosend models every product as a courier ("DHL Innosend For You Small <10kg"); the brand the warehouse
// enables for checkout is the first word of that name. warehouse-api derives it the same way.
export const checkoutBrandOf = (name) => {
    const word = String(name ?? '').trim().split(/\s+/)[0] ?? '';
    const key = checkoutCourierKey(word);
    return key ? { key, name: word } : null;
};

// Shipper types that broker several carrier brands; each enabled brand is its own checkout option.
export const CHECKOUT_BROKER_SHIPPER_TYPES = ['innosendv2', 'sendcloud'];
export const isCheckoutBroker = (type) => CHECKOUT_BROKER_SHIPPER_TYPES.includes(type);

// Sendcloud option codes are "<carrier>:<product>/<options>"; the carrier is the brand and the label's
// text before the colon is its name. warehouse-api derives the same brand.
export const sendcloudBrandOf = (option) => {
    const value = String(option?.value ?? '');
    const colon = value.indexOf(':');
    if (colon <= 0) return null;
    const carrier = value.slice(0, colon);
    const key = checkoutCourierKey(carrier);
    const label = String(option?.key ?? '');
    const name = (label.includes(':') ? label.slice(0, label.indexOf(':')) : carrier).trim() || carrier;
    return key ? { key, name, carrier } : null;
};

// The brands a broker's config info offers, in the entry shape the shipper stores per broker type.
export const checkoutBrandsFromConfigInfo = (type, configInfo) => {
    const byKey = new Map();
    if (type === 'innosendv2') {
        const methods = Array.isArray(configInfo?.configInfo?.shippingMethods) ? configInfo.configInfo.shippingMethods : [];
        for (const method of methods) {
            const brand = checkoutBrandOf(method.courierName);
            if (!brand || !method.courierUuid) continue;
            const entry = byKey.get(brand.key) ?? { key: brand.key, name: brand.name, courierUuids: [] };
            if (!entry.courierUuids.includes(method.courierUuid)) entry.courierUuids.push(method.courierUuid);
            byKey.set(brand.key, entry);
        }
    } else if (type === 'sendcloud') {
        const options = Array.isArray(configInfo?.configInfo?.shippingOptions) ? configInfo.configInfo.shippingOptions : [];
        const delivery = configInfo?.configInfo?.servicePointDelivery;
        for (const option of options) {
            const brand = sendcloudBrandOf(option);
            if (!brand || byKey.has(brand.key)) continue;
            if (delivery?.known === true) {
                const carriers = Array.isArray(delivery.carriers) ? delivery.carriers : [];
                brand.servicePoints = delivery.enabled === true && carriers.includes(brand.carrier);
            }
            byKey.set(brand.key, brand);
        }
    }
    return [...byKey.values()];
};

export const SHIPPER_REQUIRED_FIELDS = {
    postnl: ['apiKey', 'customerNumber', 'customerCode', 'mode'],
    dhl: ['key', 'userId'],
    dhlgermany: ['clientId', 'clientSecret', 'subscriptionKey', 'environment'],
    cirro: ['clientId', 'secret', 'mode'],
    parcel2send: ['shipmentMethod'],
    sendcloud: ['publicKey', 'privateKey'],
    ups: ['clientId', 'clientSecret', 'accountNumber', 'environment'],
    dpd: ['privateKey', 'publicKey'],
    innosend: ['api_key', 'secret'],
    innosendv2: ['api_token'],
    zineps: ['client_id', 'client_secret'],
    vinologix: ['accessToken'],
    parcel: ['username', 'password', 'mode']
};

export const VALIDATION_RULES = {
    postnl: {
        apiKey: { required: true, minLength: 10 },
        customerNumber: { required: true, pattern: /^\d+$/ },
        customerCode: { required: true, minLength: 2 },
        mode: { required: true, options: ['test', 'production'] }
    },
    dhl: {
        key: { required: true, minLength: 8 },
        userId: { required: true, minLength: 3 }
    },
    dhlgermany: {
        clientId: { required: true, minLength: 8 },
        clientSecret: { required: true, minLength: 8 },
        subscriptionKey: { required: true, minLength: 16 },
        environment: { required: true, options: ['sandbox', 'production'] }
    },
    cirro: {
        clientId: { required: true, minLength: 8 },
        secret: { required: true, minLength: 8 },
        mode: { required: true, options: ['test', 'production'] }
    },
    parcel2send: {
        shipmentMethod: { required: true, options: ['default', 'dpd_home', 'dpd_home_saturday'] }
    },
    sendcloud: {
        publicKey: { required: true, minLength: 16 },
        privateKey: { required: true, minLength: 16 }
    },
    ups: {
        clientId: { required: true, minLength: 16 },
        clientSecret: { required: true, minLength: 8 },
        accountNumber: { required: true, pattern: /^[A-Z0-9]{6}$/ },
        environment: { required: true, options: ['sandbox', 'production'] }
    },
    dpd: {
        privateKey: { required: true, minLength: 16 },
        publicKey: { required: true, minLength: 16 }
    },
    innosend: {
        api_key: { required: true, minLength: 8 },
        secret: { required: true, minLength: 8 }
    },
    innosendv2: {
        api_token: { required: true, minLength: 20 }
    },
    zineps: {
        client_id: { required: true, minLength: 8 },
        client_secret: { required: true, minLength: 8 }
    },
    vinologix: {
        accessToken: { required: true, minLength: 20 }
    },
    parcel: {
        username: { required: true, minLength: 3 },
        password: { required: true, minLength: 6 },
        mode: { required: true, options: ['test', 'production'] }
    }
};

export const validateShipperField = (shipperType, fieldName, value) => {
    const rules = VALIDATION_RULES[shipperType];
    if (!rules || !rules[fieldName]) return { isValid: true };

    const rule = rules[fieldName];
    const errors = [];

    // Required field validation
    if (rule.required && (!value || value.toString().trim() === '')) {
        errors.push(`${fieldName}_required`);
    }

    // Skip other validations if field is empty and not required
    if (!value || value.toString().trim() === '') {
        return { isValid: errors.length === 0, errors };
    }

    // Minimum length validation
    if (rule.minLength && value.toString().length < rule.minLength) {
        errors.push(`${fieldName}_min_length`);
    }

    // Pattern validation
    if (rule.pattern && !rule.pattern.test(value.toString())) {
        errors.push(`${fieldName}_invalid_format`);
    }

    // Options validation
    if (rule.options && !rule.options.includes(value)) {
        errors.push(`${fieldName}_invalid_option`);
    }

    return { isValid: errors.length === 0, errors };
};

export const validateShipperFormData = (shipperType, formData) => {
    const requiredFields = SHIPPER_REQUIRED_FIELDS[shipperType] || [];
    const allErrors = [];
    let isValid = true;

    // Validate required fields exist
    for (const field of requiredFields) {
        if (!formData[field] || formData[field].toString().trim() === '') {
            allErrors.push(`${field}_required`);
            isValid = false;
        } else {
            // Validate field content
            const fieldValidation = validateShipperField(shipperType, field, formData[field]);
            if (!fieldValidation.isValid) {
                allErrors.push(...fieldValidation.errors);
                isValid = false;
            }
        }
    }

    return { isValid, errors: allErrors };
};

export const getShipperDisplayName = (shipperType) => {
    const displayNames = {
        postnl: 'PostNL',
        dhl: 'DHL',
        dhlgermany: 'DHL Germany',
        cirro: 'Cirro',
        parcel2send: 'Parcel2Send',
        sendcloud: 'SendCloud',
        ups: 'UPS',
        dpd: 'DPD',
        innosend: 'InnoSend',
        innosendv2: 'InnoSend V2',
        zineps: 'Zineps',
        vinologix: 'Vinologix',
        parcel: 'Parcel'
    };
    return displayNames[shipperType] || shipperType;
};